import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@app/router/routes';
import { usePageLayout } from '@app/router';
import { getVowCategoryList, addVowRecord } from '../api/lampService';
import { getMonasteryList } from '@features/auth/api/monasteryService';
import { isAuthenticated } from '@features/auth/api/authService';
import AuthForm from '@features/auth/components/AuthForm';
import SearchableSelect from '@shared/components/ui/SearchableSelect';
import HeroBanner from '@shared/components/HeroBanner';
import './LampLightingFormPage.css';
import { useLanguage } from '@shared/contexts/LanguageContext';

const lampYellow = '/image/lamp/yellow_x2.png';
const lampOrange = '/image/lamp/orange_x2.png';
const lampRed = '/image/lamp/red_x2.png';
const lampGreen = '/image/lamp/green_x2.png';
const lampBlue = '/image/lamp/blue_x2.png';
const lampPurple = '/image/lamp/purple_x2.png';

// category_ind 對應 CSS data-blessing 的 key
const CATEGORY_COLOR_KEYS = ['health', 'family', 'dharma', 'study', 'career', 'connection'];

// 預設祈願選項（API 載入失敗時的 fallback）
const DEFAULT_BLESSING_OPTIONS = [
    { id: 1, colorKey: 'health', title: '身體健康', description: '身心安康 福壽增長' },
    { id: 2, colorKey: 'family', title: '家庭和樂', description: '和諧美滿 子孫賢孝' },
    { id: 3, colorKey: 'dharma', title: '道業成就', description: '福慧雙修 圓成佛道' },
    { id: 4, colorKey: 'study', title: '學業進步', description: '金榜題名 智慧無礙' },
    { id: 5, colorKey: 'career', title: '事業順利', description: '事事圓滿 所求皆應' },
    { id: 6, colorKey: 'connection', title: '廣結善緣', description: '人緣和合 廣修善法' },
];

const BLESSING_LAMP_IMAGES = {
    health: lampYellow,
    family: lampOrange,
    dharma: lampRed,
    study: lampGreen,
    career: lampBlue,
    connection: lampPurple,
};

/**
 * 線上祈福點燈表單頁面 - 對應 example/lamp_lighting.html
 * Lamp Lighting Form Page
 *
 * 注意：此頁面不需要包裝 PageLayout，因為 router 層級的 PageLayoutWrapper 已經提供。
 */
const LampLightingFormPage = ({
    heroData,
    // 表單提交事件
    onSubmit,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setLayoutOptions } = usePageLayout();
    const { t, currentLanguage } = useLanguage();
    const isEn = currentLanguage.code === 'en';
    const resolvedHeroData = heroData ?? {
        image: '',
        title: t('lamp-lighting.heroTitle'),
        description: t('lamp-lighting.heroDescription'),
    };
    const fromPath = location.state?.from ?? -1;

    useEffect(() => {
        setLayoutOptions({ hideHeader: true, hideFooter: true, hideBottomNav: true });
        return () => setLayoutOptions({});
    }, [setLayoutOptions]);

    const [blessingOptions, setBlessingOptions] = useState(DEFAULT_BLESSING_OPTIONS);
    const [monasteryList, setMonasteryList] = useState([]);
    const [selectedMonastery, setSelectedMonastery] = useState(null);
    const [monasteryError, setMonasteryError] = useState(false);
    const [nameError, setNameError] = useState(false);
    const [blessingError, setBlessingError] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [authMode, setAuthMode] = useState('register');
    const [authFormLoading, setAuthFormLoading] = useState(false);
    const [vowParams, setVowParams] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        blessing: null,
    });

    // 取得精舍列表 + 祈願類別 + 預填登入資訊
    useEffect(() => {
        // 精舍列表
        getMonasteryList()
            .then((list) => {
                setMonasteryList(list);

            })
            .catch((err) => {
                console.error('Failed to load monastery list:', err);
            });

        // 祈願類別
        getVowCategoryList()
            .then((res) => {
                if (res?.items?.length > 0) {
                    const options = res.items.map((item) => ({
                        id: item.category_ind,
                        colorKey: CATEGORY_COLOR_KEYS[item.category_ind - 1] || 'health',
                        title: item.title,
                        description: item.description,
                    }));
                    setBlessingOptions(options);
                }
            })
            .catch((err) => {
                console.error('Failed to load vow categories:', err);
            });
    }, []);

    const handleBlessingSelect = (blessingId) => {
        setFormData(prev => ({ ...prev, blessing: blessingId }));
        setBlessingError(false);
    };

    const handleSubmit = async () => {
        if (submitting) return;

        let hasError = false;
        if (!formData.name.trim()) { setNameError(true); hasError = true; }
        if (!selectedMonastery) { setMonasteryError(true); hasError = true; }
        if (!formData.blessing) { setBlessingError(true); hasError = true; }
        if (hasError) return;

        const params = {
            category_id: formData.blessing,
            vower_name: formData.name,
            monastery_name: selectedMonastery?.name || '',
        };

        if (isAuthenticated()) {
            // 已登入 → 直接送出
            setSubmitting(true);
            try {
                const result = await addVowRecord(params);
                navigate(ROUTES.BLESSING_RESULT, { state: { blessingResult: result, from: fromPath } });
            } catch (err) {
                console.error('Failed to submit vow record:', err);
                setSubmitting(false);
            }
        } else {
            // 未登入 → 切換到註冊畫面
            setAuthMode('register');
            setVowParams(params);
            setShowRegister(true);
        }
    };

    // 跳過註冊，直接以 viewer token 送出
    const handleSkipRegister = async () => {
        if (submitting) return;
        if (!vowParams) return;
        setShowRegister(false);
        setSubmitting(true);
        try {
            const result = await addVowRecord(vowParams);
            navigate(ROUTES.BLESSING_RESULT, { state: { blessingResult: result, from: fromPath } });
        } catch (err) {
            console.error('Failed to submit vow record:', err);
            setSubmitting(false);
        }
    };

    return (
        <>
            {/* 關閉按鈕 */}
            <button
                onClick={() => navigate(fromPath)}
                className="fixed top-4 right-4 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 transition-colors"
                aria-label={t('lamp-lighting.closeAria')}
            >
                <span className="material-symbols-outlined text-2xl text-white">close</span>
            </button>

            {/* Form Content */}
            <section
                className="lamp-lighting-page px-[7.9%] md:px-[8%] lg:px-[10%] pb-10 md:pb-14"
                data-layer="文星點燈_線上祈福點燈+"
            >
                <div className="max-w-2xl mx-auto">

                    {/* Title */}
                    <h1
                        className="text-4xl md:text-5xl font-bold mb-4 tracking-[0.2em] pt-8 md:pt-10 lg:pt-12 text-center"
                        style={{ color: 'var(--color-lamp-text)' }}
                        data-layer="線上祈福點燈"
                    >
                        {resolvedHeroData.title}
                    </h1>

                    {/* Red divider */}
                    <div
                        className="w-16 md:w-20 h-1 mx-auto mb-3"
                        style={{ background: 'var(--color-lamp-red)' }}
                        data-layer="Rectangle 4363"
                    />

                    {/* Description */}
                    <p
                        className="text-lg md:text-xl leading-relaxed whitespace-pre-line font-medium mb-3 text-center"
                        style={{ color: 'var(--color-lamp-text)' }}
                        data-layer="燃起佛前燈，滅除心頭火 願以大智慧，照破眾無明"
                    >
                        {resolvedHeroData.description}
                    </p>

                    <>
                        {/* 姓名 */}
                        <div className="mt-8 md:mt-10">
                            <label
                                className="lamp-form-label block text-[clamp(1.125rem,2.5vw,1.25rem)] font-normal mb-2 md:mb-3"
                                data-layer="姓名"
                            >
                                {t('lamp-lighting.nameLabel')}
                            </label>
                            <input
                                className={`lamp-form-input${isEn ? ' lamp-form-input--en' : ''}`}
                                data-layer="Rectangle 4379"
                                placeholder={t('lamp-lighting.namePlaceholder')}
                                type="text"
                                value={formData.name}
                                onChange={(e) => { setFormData(prev => ({ ...prev, name: e.target.value })); setNameError(false); }}
                            />
                            {nameError && (
                                <p className="mt-2 text-sm text-red-500">{t('lamp-lighting.nameRequired')}</p>
                            )}
                        </div>

                        {/* 精舍別 */}
                        <div className="mt-6 md:mt-8">
                            <label
                                className="lamp-form-label block text-[clamp(1.125rem,2.5vw,1.25rem)] font-normal mb-2 md:mb-3"
                                data-layer="精舍別"
                            >
                                {t('lamp-lighting.monasteryLabel')}
                            </label>
                            <SearchableSelect
                                className={`lamp-form-select${isEn ? ' lamp-form-select--en' : ''}`}
                                data-layer="Rectangle 4378"
                                options={monasteryList}
                                value={selectedMonastery}
                                onChange={(v) => { setSelectedMonastery(v); setMonasteryError(false); }}
                                placeholder={t('lamp.monasteryPlaceholder')}
                            />
                            {monasteryError && (
                                <p className="mt-2 text-sm text-red-500">{t('lamp-lighting.monasteryRequired')}</p>
                            )}
                        </div>

                        {/* 發願回向 */}
                        <div className="mt-8 md:mt-10">
                            <label
                                className="lamp-form-label block text-[clamp(1.125rem,2.5vw,1.25rem)] font-normal mb-4 md:mb-5"
                                data-layer="發願回向"
                            >
                                {t('lamp-lighting.blessingLabel')}
                            </label>

                            <div className="flex flex-col gap-5 md:gap-5" data-layer="Rectangle 4380">
                                {blessingOptions.map((option) => {
                                    const isSelected = formData.blessing === option.id;
                                    return (
                                        <div
                                            key={option.id}
                                            className="blessing-option"
                                            data-blessing={option.colorKey}
                                            role="radio"
                                            aria-checked={isSelected}
                                            tabIndex={0}
                                            data-layer="Rectangle 4380"
                                            onClick={() => handleBlessingSelect(option.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleBlessingSelect(option.id);
                                                }
                                            }}
                                        >
                                            {/* Radio circle */}
                                            <div
                                                className="blessing-radio"
                                                data-selected={isSelected ? 'true' : 'false'}
                                                data-layer="Ellipse 874"
                                            >
                                                <div className="blessing-radio-inner" />
                                            </div>

                                            {/* Title */}
                                            <span
                                                className={`font-normal whitespace-nowrap ${isEn ? 'text-[clamp(0.875rem,2vw,1rem)]' : 'text-[clamp(1.125rem,2.5vw,1.25rem)]'}`}
                                                data-layer="身體健康"
                                            >
                                                {option.title}
                                            </span>

                                            {/* Description - pushed to right */}
                                            {!isEn && (
                                                <span
                                                    className="text-[clamp(0.9375rem,2.2vw,1.125rem)] font-light whitespace-nowrap"
                                                    data-layer="身心安康 福壽增長"
                                                >
                                                    {option.description}
                                                </span>
                                            )}

                                            <img
                                                className="blessing-lamp-icon"
                                                style={{ marginLeft: 'auto' }}
                                                src={BLESSING_LAMP_IMAGES[option.colorKey] || lampYellow}
                                                alt=""
                                                aria-hidden="true"
                                                data-layer="Rectangle"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {blessingError && (
                                <p className="mt-2 text-sm text-red-500">{t('lamp-lighting.blessingRequired')}</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="mt-10 md:mt-12 lg:mt-14 pb-4">
                            <button
                                className="lamp-submit-btn"
                                data-layer="Rectangle 4366"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? t('lamp-lighting.submitting') : t('lamp-lighting.submit')}
                            </button>
                        </div>
                    </>
                </div>
            </section>

            {showRegister && (
                <div
                    className="fixed inset-0 z-500 flex items-center justify-center p-4"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                >
                    <div className="w-full max-w-[430px] bg-[var(--color-surface)] relative rounded-[18px] 
                      overflow-y-scroll shadow-lg h-fit flex flex-col"
                    >
                        <div className="absolute inset-0 bg-[var(--color-background)] -z-10 bg-opacity-50" />
                        <AuthForm
                            mode={authMode}
                            onLoadingChange={setAuthFormLoading}
                            onSwitchMode={() => setAuthMode((m) => (m === 'login' ? 'register' : 'login'))}
                            onSuccess={async (user) => {
                                if (!vowParams) return;
                                setSubmitting(true);
                                try {
                                    const result = await addVowRecord({
                                        ...vowParams,
                                        vower_name: user?.name,
                                        monastery_name: user?.monasteryName,
                                    });
                                    setShowRegister(false);
                                    navigate(ROUTES.BLESSING_RESULT, { state: { blessingResult: result, from: fromPath } });
                                } catch (err) {
                                    console.error('Registration or submit failed:', err);
                                    alert(t('lamp-lighting.operationFailed').replace('{{message}}', err.message || t('lamp-lighting.tryAgainLater')));
                                    setSubmitting(false);
                                }
                            }}
                        />

                        <div className="px-5 pb-6 pt-0 flex justify-center">
                            <button
                                className="lamp-skip-btn"
                                style={{ background: 'transparent', color: '#B34F4F', border: '1px solid #B34F4F' }}
                                data-layer="Rectangle 4366"
                                onClick={handleSkipRegister}
                                disabled={submitting || authFormLoading}
                            >
                                {submitting ? t('lamp-lighting.skipProcessing') : t('lamp-lighting.skip')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

LampLightingFormPage.propTypes = {
    heroData: PropTypes.shape({
        image: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
    }),
    onSubmit: PropTypes.func,
};

export default LampLightingFormPage;
